import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import styles from './ScorecardStrip.module.css';

// Material Icons Round "info_outline" glyph: ring, rounded stem, dot (no icon dependency, see TruckIcon).
const INFO_PATH =
  'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-9c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1s1-.45 1-1v-4c0-.55-.45-1-1-1zm0-4.25a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 1 0 0-2.5z';

export function ChipInfo({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [alignRight, setAlignRight] = useState(false);
  const popId = useId();

  // Keep the popover inside the viewport: flip to right-aligned when it would overflow.
  useLayoutEffect(() => {
    if (!open) {
      setAlignRight(false);
      return;
    }
    if (!popRef.current) return;
    const rect = popRef.current.getBoundingClientRect();
    setAlignRight(
      rect.right > window.innerWidth - 8 &&
        rect.width <= (buttonRef.current?.getBoundingClientRect().right ?? 0),
    );
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span className={styles.chipInfoWrap} ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.chipInfoBtn}
        aria-label={`About ${label}`}
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <svg
          className={styles.chipInfoIcon}
          width={18}
          height={18}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          focusable="false"
        >
          <path d={INFO_PATH} />
        </svg>
      </button>
      {open && (
        <div
          ref={popRef}
          id={popId}
          role="dialog"
          aria-label={label}
          className={`${styles.chipInfoPop} ${alignRight ? styles.chipInfoPopRight : ''}`}
        >
          {text}
        </div>
      )}
    </span>
  );
}
